import React, { useCallback, useState } from 'react';

import { Skeleton, Spin } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { Link } from 'react-router-dom';

import { getAccountPartnershipsWithRoles } from '../api/getAccountPartnershipsWithRoles';
import { IPartnershipWithRole } from '../types/partnership';
import StyledTable from '@/components/tables/StyledTable';
import { PartnerTypeBadge } from '@/features/lp-village/partners/components/PartnerTypeBadge';
import { getPartnerHierarchyTypeFromList } from '@/features/lp-village/partners/helpers/partnerHierarchy';
import { getCurrentPage, getOffset } from '@/helpers/antdTable';
import useAntdColumns from '@/hooks/useAntdColumns';

const DEFAULT_PAGE_SIZE = 10;

const SKELETON_ROWS: IPartnershipWithRole[] = Array.from(
  { length: DEFAULT_PAGE_SIZE },
  (_, i) => ({
    id: `skeleton-${i}`,
    name: '',
    type: '',
    internal: false,
    archived: false,
    deleted: false,
    createdAt: 0,
    updatedAt: 0,
    roleLoading: false,
  })
);

export const LPAccountPartnershipsTable = ({ id }: { id: string }) => {
  // pageSize and offset must precede the domain hook as they are passed as params
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  const { data, total, isLoading } = getAccountPartnershipsWithRoles({
    accountId: id,
    pageSize,
    offset,
  });

  const tableData = isLoading ? SKELETON_ROWS : data;

  const columns: ColumnProps<IPartnershipWithRole>[] = useAntdColumns({
    columnsKeys: ['partnerName', 'type', 'roleName'],
    columnsSpecialProps: {
      partnerName: {
        render: (_: string, record: IPartnershipWithRole) => {
          if (isLoading) return <Skeleton.Input active size="small" block />;

          return (
            <Link to={`/lp-village/partners/${record.id}`}>{record.name}</Link>
          );
        },
      },
      type: {
        render: (_: string, record: IPartnershipWithRole) => {
          if (isLoading) return <Skeleton.Input active size="small" block />;

          return (
            <PartnerTypeBadge
              partnerType={getPartnerHierarchyTypeFromList(record)}
            />
          );
        },
      },
      roleName: {
        render: (value: string | undefined, record: IPartnershipWithRole) => {
          if (isLoading) return <Skeleton.Input active size="small" block />;
          if (record.roleLoading) return <Spin size="small" />;

          return value ?? '—';
        },
      },
    },
    addDefaultColumns: false,
    defaultSort: {
      sortBy: 'updatedAt',
      order: 'DESC',
    },
  });

  const pagination = {
    current: getCurrentPage(pageSize, offset),
    pageSize,
    total,
    showSizeChanger: false,
  };

  const handleTableChange = useCallback(
    (
      newPagination: { pageSize: number; current: number },
      _filters: unknown,
      _sorter: unknown
    ) => {
      setOffset(getOffset(newPagination.pageSize, newPagination.current));
      setPageSize(newPagination.pageSize);
    },
    []
  );

  return (
    <StyledTable
      rowKey="id"
      dataSource={tableData}
      columns={columns}
      size="small"
      pagination={pagination}
      onChange={handleTableChange}
      scroll={{ x: 'max-content' }}
    />
  );
};
